/**
 * Normalizes a string to Title Case format (first letter uppercase, rest lowercase)
 * Useful for normalizing enum values or status strings from APIs
 *
 * @param str - The string to normalize
 * @returns The normalized string in Title Case format, or empty string if input is falsy
 *
 * @example
 * normalizeToTitleCase('employee') // 'Employee'
 * normalizeToTitleCase('EQUIPMENT') // 'Equipment'
 * normalizeToTitleCase('Employee') // 'Employee'
 * normalizeToTitleCase('') // ''
 */
export const normalizeToTitleCase = (str: string | null | undefined): string => {
  if (!str) return '';

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Splits a full name into firstName and lastName
 * First word becomes firstName, remaining words become lastName
 *
 * @param fullName - The full name string to split
 * @returns An object with firstName and lastName properties
 *
 * @example
 * splitFullName('John Doe') // { firstName: 'John', lastName: 'Doe' }
 * splitFullName('Mary Jane Watson') // { firstName: 'Mary', lastName: 'Jane Watson' }
 * splitFullName('John') // { firstName: 'John', lastName: '' }
 * splitFullName('') // { firstName: '', lastName: '' }
 */
export const splitFullName = (fullName: string | null | undefined): { firstName: string; lastName: string } => {
  if (!fullName) {
    return { firstName: '', lastName: '' };
  }

  const trimmedName = fullName.trim();
  if (!trimmedName) {
    return { firstName: '', lastName: '' };
  }

  const nameParts = trimmedName.split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return { firstName, lastName };
};

/**
 * Email validation regex pattern
 * Matches standard email format: local-part@domain.tld
 * Does not allow spaces in the email address
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Phone validation regex pattern
 * Matches phone numbers that contain at least one digit and no letters
 * Allows common phone number characters like spaces, dashes, parentheses, plus signs, etc.
 */
export const PHONE_REGEX = /^(?=.*[0-9])[^a-zA-Z]*$/;

/**
 * Converts a name from "Last Name, First Name" format to "First Name Last Name" format
 * If the name doesn't contain a comma, returns it as-is
 *
 * @param name - The name string to convert (may be in "Last, First" or "First Last" format)
 * @returns The name in "First Last" format without comma
 *
 * @example
 * formatNameWithoutComma('Doe, John') // 'John Doe'
 * formatNameWithoutComma('John Doe') // 'John Doe'
 * formatNameWithoutComma('Admin, Raptor') // 'Raptor Admin'
 * formatNameWithoutComma('') // ''
 */
export const formatNameWithoutComma = (name: string | null | undefined): string => {
  if (!name) return '';

  const trimmedName = name.trim();
  if (!trimmedName) return '';

  // Check if name contains a comma (format: "Last, First")
  if (trimmedName.includes(',')) {
    const parts = trimmedName
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      // Reverse: [Last, First] -> "First Last"
      return [...parts.slice(1), parts[0]].join(' ');
    }
  }

  // If no comma, return as-is
  return trimmedName;
};

/**
 * Validates if a string is a valid email format
 *
 * @param email - The email string to validate
 * @returns true if the email format is valid, false otherwise
 *
 * @example
 * isValidEmail('test@example.com') // true
 * isValidEmail('invalid-email') // false
 * isValidEmail('') // false
 */
export const isValidEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  return EMAIL_REGEX.test(email);
};

type NormalizeOptions = {
  preserveUndefined?: boolean;
};

export const normalizeCallerId = (value: number | null | undefined | string, options: NormalizeOptions = {}): number | null | undefined => {
  if (value === undefined) {
    return options.preserveUndefined ? undefined : null;
  }
  if (value === null || value === '' || value === 0) {
    return null;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return value;
};

/**
 * Converts empty strings to null in an object
 * Recursively processes object properties, converting empty strings ('') to null
 * Useful for API payloads that require null instead of empty strings
 *
 * @param obj - The object to process
 * @returns A new object with empty strings converted to null
 *
 * @example
 * convertEmptyStringsToNull({ name: '', description: 'test', value: null })
 * // { name: null, description: 'test', value: null }
 */
export const convertEmptyStringsToNull = <T>(obj: T): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle primitives (except strings which are handled below)
  if (typeof obj !== 'object') {
    return obj;
  }

  // Handle Date objects
  if (obj instanceof Date) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) => convertEmptyStringsToNull(item)) as T;
  }

  // Handle objects
  const result = { ...obj } as Record<string, unknown>;

  for (const key in result) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const value = result[key];

      // Convert empty strings to null
      if (value === '') {
        result[key] = null;
      }
      // Recursively process nested objects and arrays
      else {
        result[key] = convertEmptyStringsToNull(value);
      }
    }
  }

  return result as T;
};
